import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Leaf, ArrowLeft } from 'lucide-react';

const FAQ_DATA = [
  {
    question: "What is WeederBoard?",
    answer: "WeederBoard is a cannabis deal aggregation platform that helps you find the best prices on cannabis products across multiple dispensaries. We track deals daily and provide advanced filtering to help you save money on your favorite strains and products."
  },
  {
    question: "How does WeederBoard work?",
    answer: "We scrape pricing data from participating dispensaries daily and organize it into an easy-to-browse leaderboard format. Our premium members get access to advanced filtering by strain, brand, dispensary, price range, and more."
  },
  {
    question: "Is WeederBoard free to use?",
    answer: "We offer a limited free preview of today's top deals. For full access to all deals, advanced filtering, and daily updates, we offer a premium subscription for just $4.20 per month."
  },
  {
    question: "Which dispensaries do you track?",
    answer: "We currently track deals from major dispensary chains and independent shops across legal cannabis states. Our network is constantly growing, and we add new dispensaries regularly based on user requests."
  },
  {
    question: "How often are deals updated?",
    answer: "We update our deal database daily to ensure you're seeing the most current pricing and availability."
  },
  {
    question: "Can I filter deals by specific criteria?",
    answer: "Yes! Premium members can filter deals by strain name, brand, dispensary, price range, THC percentage, product type (flower, edibles, concentrates), and more. This helps you quickly find exactly what you're looking for."
  },
  {
    question: "Is my personal information secure?",
    answer: "Absolutely. We take privacy seriously and use industry-standard encryption to protect your data. We never share your personal information with third parties, and you can delete your account at any time."
  },
  {
    question: "Can I cancel my subscription anytime?",
    answer: "Yes, you can cancel your premium subscription at any time through your account settings. There are no long-term contracts or cancellation fees."
  },
  {
    question: "Do you have a mobile app?",
    answer: "Currently, WeederBoard is available as a responsive web application that works great on mobile devices."
  },
  {
    question: "How do I report incorrect pricing?",
    answer: "If you notice incorrect pricing or outdated deals, please contact our support team. We work quickly to verify and update any discrepancies to maintain the accuracy of our platform."
  },
  {
    question: "Can dispensaries partner directly with WeederBoard?",
    answer: "Yes! We're always looking to partner with dispensaries to provide better deal coverage. Dispensary owners can contact us through our partnership program to learn about featuring their deals prominently on our platform."
  }
];

const FAQItem = ({ faq, index, isOpen, onToggle }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300">
      <button
        onClick={() => onToggle(index)}
        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 rounded-xl transition-colors duration-200"
      >
        <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
        {isOpen ? (
          <ChevronUp className="text-green-600 flex-shrink-0" size={20} />
        ) : (
          <ChevronDown className="text-green-600 flex-shrink-0" size={20} />
        )}
      </button>
      {isOpen && (
        <div className="px-6 pb-4">
          <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
        </div>
      )}
    </div>
  );
};

const FAQ = ({ onBack }) => {
  const [openItems, setOpenItems] = useState(new Set([0])); // First item open by default

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="flex items-center space-x-2 text-gray-600 hover:text-green-600 transition-colors"
              >
                <ArrowLeft size={20} />
                <span>Back</span>
              </button>
              <div className="flex items-center space-x-2">
                <Leaf className="text-green-600" size={32} />
                <span className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                  WeederBoard
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-6">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-gray-600 mb-12">
              Find answers to common questions about WeederBoard and how we help you save money on cannabis products.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto space-y-4">
            {FAQ_DATA.map((faq, index) => (
              <FAQItem
                key={index}
                faq={faq}
                index={index}
                isOpen={openItems.has(index)}
                onToggle={toggleItem}
              />
            ))}
          </div>

          {/* Contact Section */}
          <div className="max-w-3xl mx-auto mt-16 text-center">
            <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Still have questions?
              </h2>
              <p className="text-gray-600 mb-6">
                Can't find the answer you're looking for? Our support team is here to help.
              </p>
              <a href="/contact" className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300">
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Leaf className="text-green-400" size={24} />
            <span className="text-xl font-bold">WeederBoard</span>
          </div>
          <p className="text-gray-400">
            The smartest way to find deals on cannabis products and save money.
          </p>
          <div className="border-t border-gray-800 mt-8 pt-8">
            <p className="text-gray-400">&copy; 2025 WeederBoard. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FAQ;
